/**
 * LiveFunnelWidget - Horizontal user journey funnel in UnCloned brand colors
 * Shows real-time visitors flowing through the app journey from left to right
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  Home, 
  LogIn, 
  UserPlus, 
  ClipboardList, 
  BookOpen,
  RefreshCw,
  ChevronRight,
  Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_INTERVAL = 5000; // 5 seconds

const LiveFunnelWidget = () => {
  const [funnel, setFunnel] = useState({
    landing: 0,
    signing_in: 0,
    signing_up: 0,
    onboarding: 0,
    journaling: 0
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchFunnel = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/live-funnel`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFunnel(data.funnel);
          setTotal(data.total);
          setLastUpdated(new Date());
        }
      }
    } catch (error) {
      console.error('Failed to fetch live funnel:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFunnel();
    const interval = setInterval(fetchFunnel, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchFunnel]);

  // Stage configuration with UnCloned brand colors
  const stages = [
    { 
      key: 'landing', 
      label: 'Landing', 
      icon: Home, 
      color: '#0FFFE2', // cyan
      bgColor: 'bg-[#0FFFE2]/20',
      borderColor: 'border-[#0FFFE2]/50'
    },
    { 
      key: 'signing_in', 
      label: 'Signing In', 
      icon: LogIn, 
      color: '#df1283', // pink
      bgColor: 'bg-[#df1283]/20',
      borderColor: 'border-[#df1283]/50'
    },
    { 
      key: 'signing_up', 
      label: 'Signing Up', 
      icon: UserPlus, 
      color: '#FF7901', // orange
      bgColor: 'bg-[#FF7901]/20',
      borderColor: 'border-[#FF7901]/50'
    },
    { 
      key: 'onboarding', 
      label: 'Onboarding', 
      icon: ClipboardList, 
      color: '#df1283', // pink
      bgColor: 'bg-[#df1283]/20',
      borderColor: 'border-[#df1283]/50'
    },
    { 
      key: 'journaling', 
      label: 'Journaling', 
      icon: BookOpen, 
      color: '#22C55E', // green (success)
      bgColor: 'bg-[#22C55E]/20',
      borderColor: 'border-[#22C55E]/50'
    }
  ];

  return (
    <div 
      className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-6"
      data-testid="live-funnel-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Users className="w-5 h-5 text-[#0FFFE2]" />
            {/* Live indicator */}
            <div className="absolute -top-1 -right-1 w-2.5 h-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#22C55E] opacity-75 animate-ping"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#22C55E]"></span>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Live User Journey</h3>
            <p className="text-xs text-[#FDF2F8]/50">
              {loading ? 'Loading...' : `${total} active visitor${total !== 1 ? 's' : ''} right now`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-[#FDF2F8]/40">
              {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button 
            onClick={fetchFunnel}
            className="p-1.5 rounded-lg hover:bg-[#4A0A66]/50 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 text-[#FDF2F8]/60 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Horizontal Journey Flow */}
      <div className="relative">
        {/* Connection line */}
        <div className="absolute top-1/2 left-8 right-8 h-0.5 bg-gradient-to-r from-[#0FFFE2]/30 via-[#df1283]/30 to-[#22C55E]/30 -translate-y-1/2 z-0" />
        
        {/* Stages */}
        <div className="relative z-10 flex items-center justify-between px-2">
          {stages.map((stage, index) => {
            const count = funnel[stage.key] || 0;
            const Icon = stage.icon;
            const hasVisitors = count > 0;
            
            return (
              <div key={stage.key} className="flex items-center flex-1">
                {/* Stage Node */}
                <div className="flex flex-col items-center flex-1">
                  {/* Count Badge */}
                  <div 
                    className={`
                      mb-2 min-w-[32px] px-2 py-0.5 rounded-full text-sm font-bold text-center
                      transition-all duration-300
                      ${hasVisitors 
                        ? 'text-white shadow-lg scale-110' 
                        : 'bg-[#4A0A66]/30 text-[#FDF2F8]/40'
                      }
                    `}
                    style={hasVisitors ? { 
                      backgroundColor: stage.color,
                      boxShadow: `0 0 15px ${stage.color}60`
                    } : {}}
                  >
                    {count}
                  </div>
                  
                  {/* Icon Circle */}
                  <div 
                    className={`
                      w-12 h-12 rounded-full flex items-center justify-center
                      border-2 transition-all duration-300
                      ${hasVisitors 
                        ? `${stage.bgColor} ${stage.borderColor}` 
                        : 'bg-[#4A0A66]/20 border-[#4A0A66]/30'
                      }
                    `}
                    style={hasVisitors ? { 
                      boxShadow: `0 0 20px ${stage.color}30`
                    } : {}}
                  >
                    <Icon 
                      className={`w-5 h-5 transition-colors duration-300`}
                      style={{ color: hasVisitors ? stage.color : '#FDF2F8' + '40' }}
                    />
                  </div>
                  
                  {/* Label */}
                  <span 
                    className={`
                      mt-2 text-xs font-medium transition-colors duration-300
                      ${hasVisitors ? 'text-white' : 'text-[#FDF2F8]/40'}
                    `}
                  >
                    {stage.label}
                  </span>
                </div>
                
                {/* Arrow Connector (except last) */}
                {index < stages.length - 1 && (
                  <ChevronRight 
                    className={`w-4 h-4 flex-shrink-0 -mt-5 transition-colors duration-300 ${
                      hasVisitors || funnel[stages[index + 1]?.key] > 0
                        ? 'text-[#FDF2F8]/30' 
                        : 'text-[#FDF2F8]/10'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Row */}
      <div className="mt-5 pt-4 border-t border-[#4A0A66]/30 flex items-center justify-center gap-8">
        <div className="text-center">
          <div className="text-xl font-bold text-white">{total}</div>
          <div className="text-[10px] text-[#FDF2F8]/50 uppercase tracking-wide">Total Active</div>
        </div>
        <div className="w-px h-8 bg-[#4A0A66]/50" />
        <div className="text-center">
          <div className="text-xl font-bold text-[#0FFFE2]">{funnel.landing}</div>
          <div className="text-[10px] text-[#FDF2F8]/50 uppercase tracking-wide">Browsing</div>
        </div>
        <div className="w-px h-8 bg-[#4A0A66]/50" />
        <div className="text-center">
          <div className="text-xl font-bold text-[#df1283]">{funnel.signing_in + funnel.signing_up}</div>
          <div className="text-[10px] text-[#FDF2F8]/50 uppercase tracking-wide">Auth Flow</div>
        </div>
        <div className="w-px h-8 bg-[#4A0A66]/50" />
        <div className="text-center">
          <div className="text-xl font-bold text-[#22C55E]">{funnel.journaling}</div>
          <div className="text-[10px] text-[#FDF2F8]/50 uppercase tracking-wide">Active Users</div>
        </div>
      </div>
    </div>
  );
};

export default LiveFunnelWidget;
